import React, { useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Sparkles, CheckCircle2, X } from 'lucide-react';
import { S3Image } from '../../lib/s3';
import ReplayTourButton from '../Shared/ReplayTourButton/ReplayTourButton';
import useVideoPosterFrame from './useVideoPosterFrame';

const STORAGE_KEY_PREFIX = 'succession_quickstart_welcome_dismissed_';

/**
 * Motivation-specific content configuration.
 * Maps each motivation value from the onboarding modal to a tailored welcome
 * message with heading, subtitle, and three feature highlights that align with
 * the activities shown in the quickstart guide for that motivation.
 *
 * When `simpleMarketplaceApprovalEnabled` is true, the default Deal Room /
 * Plan Builder narrative is replaced with a marketplace-oriented one that
 * matches the stripped-down activities available under the simple marketplace
 * approval flow (no Deal Room, no Plan Builder; Rolodex and search-based EOI
 * take their place).
 */
const getMotivationContent = (motivation, dealRoomBrandedName = 'Deal Room', intl, simpleMarketplaceApprovalEnabled = false) => {
  if (simpleMarketplaceApprovalEnabled && motivation === 'growth') {
    return {
      subtitle: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_subtitle' }),
      features: [
        { title: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_feature1_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_feature1_desc' }) },
        { title: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_feature2_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_feature2_desc' }) },
        { title: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_feature3_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_buyer_feature3_desc' }) },
      ],
    };
  }

  if (simpleMarketplaceApprovalEnabled && motivation === 'succession') {
    return {
      subtitle: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_subtitle' }),
      features: [
        { title: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_feature1_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_feature1_desc' }) },
        { title: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_feature2_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_feature2_desc' }) },
        { title: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_feature3_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_marketplace_seller_feature3_desc' }) },
      ],
    };
  }

  switch (motivation) {
    case 'succession':
      return {
        subtitle: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_subtitle' }),
        features: [
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_feature1_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_feature1_desc' }), isValuation: true },
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_feature2_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_feature2_desc' }) },
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_feature3_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_succession_feature3_desc' }, { dealRoomBrandedName }) },
        ],
      };
    case 'growth':
      return {
        subtitle: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_subtitle' }),
        features: [
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_feature1_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_feature1_desc' }, { dealRoomBrandedName }) },
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_feature2_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_feature2_desc' }) },
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_feature3_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_growth_feature3_desc' }) },
        ],
      };
    default:
      return {
        subtitle: intl.formatMessage({ id: 'succession.launchpad.welcome_default_subtitle' }),
        features: [
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_default_feature1_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_default_feature1_desc' }) },
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_default_feature2_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_default_feature2_desc' }), isValuation: true },
          { title: intl.formatMessage({ id: 'succession.launchpad.welcome_default_feature3_title' }), description: intl.formatMessage({ id: 'succession.launchpad.welcome_default_feature3_desc' }) },
        ],
      };
  }
};

/**
 * QuickstartWelcomeMessage
 *
 * A first-visit onboarding card displayed at the top of the LaunchPad when the
 * user has 0% quickstart completion (immediately after signup or completing the
 * motivation onboarding modal). It is also shown to EXISTING users on their first
 * visit to the modernized LaunchPad — even with carried-over legacy progress —
 * when the `launchpad_welcome_backfill` feature is enabled (the parent LaunchPad
 * decides this via the server-tracked `first_launchpad_visit`). The content is
 * personalized based on the user's chosen motivation and aligns with the
 * quickstart guide activities.
 *
 * Theming: Uses succession CSS custom-property tokens (--succession-primary,
 * --succession-primary-rgb, etc.) so the card automatically inherits the
 * company's brand colors and respects light/dark mode.
 *
 * Dismissal persists to localStorage keyed per-user so it only appears once.
 *
 * Props:
 * - userName: string          — User's display name for the greeting
 * - companyName: string       — Company branded name for the heading
 * - companyRawName: string    — Company.name used to build S3 logomark filename
 * - dealRoomBrandedName: string — Company-specific label for the deal room
 * - motivation: string|null   — 'succession' | 'growth' | 'no_motivation' | null
 * - consultationUrl: string   — URL for "Book a consultation" link
 * - userId: string|number     — Used to namespace the localStorage dismiss key
 * - className: string         — Additional wrapper classes
 * - onDismiss: () => void     — Optional callback fired when the message is dismissed
 */
const QuickstartWelcomeMessage = ({
  userName = 'there',
  companyName = 'FindBob',
  companyRawName = 'FindBob',
  dealRoomBrandedName = 'Deal Room',
  motivation,
  consultationUrl,
  userId,
  simpleMarketplaceApprovalEnabled = false,
  valuationEnabled = true,
  className = '',
  onDismiss,
  // Welcome video integration — controlled from LaunchPad.
  // showVideo: feature flag is on AND company has a welcome_video_url.
  // videoWatched: whether the user has finished the video at least once.
  // videoUrl: the URL to play inline (companies.welcome_video_url).
  // onVideoWatched: fires when the user has watched >=80% / video ended.
  showVideo = false,
  videoWatched = false,
  videoUrl = null,
  onVideoWatched,
}) => {
  const videoRef = useRef(null);
  // Capture frame 20 as the poster image (≈0.67s at 30fps). Falls back to the
  // browser's default first-frame if CORS blocks canvas extraction.
  const videoPoster = useVideoPosterFrame(showVideo ? videoUrl : null);
  const handleVideoProgress = () => {
    if (!videoRef.current || !onVideoWatched) return;
    const v = videoRef.current;
    if (!v.duration) return;
    if ((v.currentTime / v.duration) * 100 >= 80) onVideoWatched();
  };
  const intl = useIntl();
  const [logoError, setLogoError] = useState(false);
  const logomarkUrl = S3Image(`${companyRawName}_logomark.png`);
  const storageKey = `${STORAGE_KEY_PREFIX}${userId || 'anon'}`;

  const [isVisible, setIsVisible] = useState(() => {
    try {
      return localStorage.getItem(storageKey) !== 'true';
    } catch (_) {
      return true;
    }
  });

  const handleDismiss = () => {
    setIsVisible(false);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (_) {
      // localStorage unavailable — component simply hides for this session
    }
    onDismiss?.();
  };

  if (!isVisible) return null;

  const content = getMotivationContent(motivation, dealRoomBrandedName, intl, simpleMarketplaceApprovalEnabled);
  // Hide valuation highlights for tenants whose subscription doesn't include valuation.
  const visibleFeatures = content.features.filter((feature) => valuationEnabled || !feature.isValuation);

  return (
    <div
      className={`relative rounded-xl border border-succession-border overflow-hidden ${className}`}
      style={{
        background:
          'linear-gradient(to bottom right, rgba(var(--succession-primary-rgb, 84,137,182), 0.08), rgba(var(--succession-secondary-rgb, 0,41,73), 0.06))',
      }}
    >
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 p-1.5 rounded-lg transition-colors hover:bg-succession-bg-tertiary z-10"
        aria-label={intl.formatMessage({ id: 'succession.launchpad.welcome_dismiss_label' })}
      >
        <X className="w-5 h-5 text-succession-text-secondary" />
      </button>

      <div className="p-6 md:p-8">
        {/* Header with company logomark or fallback sparkles icon */}
        <div className="flex items-start gap-4 mb-8">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{
              backgroundColor: 'rgba(var(--succession-primary-rgb, 84,137,182), 0.15)',
              color: 'var(--succession-primary, #5489B6)',
            }}
          >
            {!logoError ? (
              <img
                src={logomarkUrl}
                alt={`${companyName} logo`}
                className="w-8 h-8 object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <Sparkles className="w-6 h-6" />
            )}
          </div>
          <div className="flex-1 pr-8">
            <h2 className="text-xl font-bold text-succession-text-primary mb-3">
              {intl.formatMessage({ id: 'succession.launchpad.welcome_to_company' }, { companyName, userName })}
            </h2>
            <p className="text-sm text-succession-text-secondary">{content.subtitle}</p>
          </div>
        </div>

        {/* Inline welcome video — plays directly in the welcome card.
            Native <video controls> gives us first-frame poster, play button,
            scrubbing, mute, fullscreen, etc. for free. Watched badge overlays
            once playback hits 80% or the video ends. */}
        {showVideo && videoUrl && (
          <div
            style={{ aspectRatio: '16 / 9' }}
            className="relative w-full mb-6 rounded-xl overflow-hidden border border-succession-border shadow-succession-md bg-black"
          >
            <video
              ref={videoRef}
              src={videoUrl}
              poster={videoPoster || undefined}
              controls
              preload="metadata"
              playsInline
              className="w-full h-full object-cover bg-black"
              onTimeUpdate={handleVideoProgress}
              onEnded={onVideoWatched}
            />
            {videoWatched && (
              <div
                className="pointer-events-none absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-medium backdrop-blur-sm"
                style={{ backgroundColor: 'rgba(34,197,94,0.9)' }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {intl.formatMessage({ id: 'succession.launchpad.welcome_video_watched_badge' })}
              </div>
            )}
          </div>
        )}

        {/* Three-column feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          {visibleFeatures.map((feature, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <CheckCircle2
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--succession-primary, #5489B6)' }}
              />
              <div className="space-y-1">
                <p className="font-medium text-sm text-succession-text-primary">{feature.title}</p>
                <p className="text-xs text-succession-text-secondary">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer actions */}
        <div
          className="flex flex-wrap items-center gap-3 pt-4"
          style={{
            borderTop: '1px solid rgba(var(--succession-primary-rgb, 84,137,182), 0.15)',
          }}
        >
          <ReplayTourButton />
          {consultationUrl && (
            <>
              <a
                href={consultationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium transition-opacity"
                style={{ color: 'var(--succession-primary, #5489B6)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {intl.formatMessage({ id: 'succession.launchpad.welcome_book_consultation' })}
              </a>
              <span className="text-succession-text-secondary">&middot;</span>
            </>
          )}
          <button
            onClick={handleDismiss}
            className="text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors"
          >
            {intl.formatMessage({ id: 'succession.launchpad.welcome_dismiss_banner' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickstartWelcomeMessage;
